'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, Truck, ShoppingBag, Upload, CheckCircle, AlertTriangle, Mail, Phone, Warehouse, Building2, Package, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

type Role = 'traveler' | 'customer';
type CustomerType = 'parcel_sender' | 'storage_booker';

interface SignupInputs {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  idType: string;
  termsAccepted: boolean;
  courierTerms: boolean;
}

interface SignupFormProps {
  onSwitchToLogin: () => void;
  onHostSignup?: () => void;
}

export default function SignupForm({ onSwitchToLogin, onHostSignup }: SignupFormProps) {
  const router = useRouter();
  const { signUp } = useAuth();
  const supabase = createClient();
  const [role, setRole] = useState<Role>('traveler');
  const [customerType, setCustomerType] = useState<CustomerType>('parcel_sender');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [idFileName, setIdFileName] = useState<string | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationMode, setVerificationMode] = useState<'email' | 'phone' | 'both'>('email');

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignupInputs>({ defaultValues: { idType: 'national_id' } });

  const password = watch('password');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await supabase
          .from('admin_settings')
          .select('key, value')
          .in('key', ['verification_require_email', 'verification_require_phone', 'verification_mode']);
        if (data) {
          const mode = data.find((s: any) => s.key === 'verification_mode')?.value;
          if (mode === 'email' || mode === 'phone' || mode === 'both') {
            setVerificationMode(mode);
          }
        }
      } catch {
        // Use default
      }
    };
    fetchSettings();
  }, []);

  const onSubmit = async (data: SignupInputs) => {
    try {
      const result = await signUp(data.email, data.password, {
        fullName: data.fullName,
        role,
        phone: data.phone,
        customer_type: role === 'customer' ? customerType : null,
      });

      // Save customer_type to user_profiles after signup
      if (result?.user?.id && role === 'customer') {
        try {
          await supabase
            .from('user_profiles')
            .update({ customer_type: customerType })
            .eq('id', result.user.id);
        } catch {
          // Non-critical, will be set on next profile load
        }
      }

      setVerificationSent(true);
      toast.success('Account created! Please verify your account.');
    } catch (err: any) {
      setError('root', {
        message: err?.message || 'Failed to create account. Please try again.',
      });
    }
  };

  if (verificationSent) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 rounded-full bg-positive-subtle flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-positive" />
        </div>
        <h2 className="text-xl font-700 text-foreground mb-2">Account Created!</h2>
        {(verificationMode === 'email' || verificationMode === 'both') && (
          <div className="flex items-center gap-2 justify-center mb-2">
            <Mail size={16} className="text-primary" />
            <p className="text-sm text-muted-foreground">Check your email to verify your account</p>
          </div>
        )}
        {(verificationMode === 'phone' || verificationMode === 'both') && (
          <div className="flex items-center gap-2 justify-center mb-2">
            <Phone size={16} className="text-primary" />
            <p className="text-sm text-muted-foreground">A verification SMS has been sent to your phone</p>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-3 mb-6">
          {verificationMode === 'both' ?'Please verify both your email and phone number to complete registration.'
            : `Please verify your ${verificationMode} to complete registration.`}
        </p>
        <button
          onClick={() => { router.push('/sign-up-login-screen'); router.refresh(); }}
          className="px-6 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-700 hover:opacity-90 transition-colors"
        >
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <h1 className="text-2xl font-700 text-foreground mb-1">Create your account</h1>
      <p className="text-sm text-muted-foreground mb-5">Join Kenya's travel marketplace — travelers helping travelers</p>

      {errors.root && (
        <div className="mb-4 px-4 py-3 bg-negative-subtle border border-red-200 rounded-xl text-sm text-negative font-500">
          {errors.root.message}
        </div>
      )}

      {/* Verification mode notice */}
      <div className="mb-4 flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl">
        {verificationMode === 'email' && <Mail size={14} className="text-blue-600 shrink-0 mt-0.5" />}
        {verificationMode === 'phone' && <Phone size={14} className="text-blue-600 shrink-0 mt-0.5" />}
        {verificationMode === 'both' && <CheckCircle size={14} className="text-blue-600 shrink-0 mt-0.5" />}
        <p className="text-xs text-blue-700">
          {verificationMode === 'email' && 'Email verification required after signup.'}
          {verificationMode === 'phone' && 'Phone number verification required after signup.'}
          {verificationMode === 'both' && 'Both email and phone verification required after signup.'}
        </p>
      </div>

      {/* Role selector */}
      <div className="mb-5">
        <label className="block text-sm font-600 text-foreground mb-2">I want to join as</label>
        <p className="text-xs text-muted-foreground mb-3">You can switch roles after sign-up</p>
        <div className="grid grid-cols-2 gap-3">
          {([
            { id: 'traveler', label: 'Traveler', sub: 'Earn by carrying packages on my trips', icon: Truck },
            { id: 'customer', label: 'Customer', sub: 'Send parcels or book storage space', icon: ShoppingBag },
          ] as const).map((r) => (
            <button
              key={`role-${r.id}`}
              type="button"
              onClick={() => setRole(r.id)}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-150 ${
                role === r.id
                  ? 'border-primary bg-secondary' : 'border-border hover:border-muted-foreground'
              }`}
            >
              <div className={`p-2 rounded-lg ${role === r.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                <r.icon size={18} />
              </div>
              <div className="text-center">
                <p className={`text-sm font-700 ${role === r.id ? 'text-primary' : 'text-foreground'}`}>{r.label}</p>
                <p className="text-xs text-muted-foreground">{r.sub}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Customer type selector — only shown when customer role is selected */}
        {role === 'customer' && (
          <div className="mt-4">
            <label className="block text-sm font-600 text-foreground mb-2">What would you like to do?</label>
            <div className="grid grid-cols-2 gap-3">
              {([
                {
                  id: 'parcel_sender' as CustomerType,
                  label: 'Send a Parcel',
                  sub: 'Ship packages via travelers',
                  icon: Package,
                  color: 'from-blue-500 to-indigo-600',
                },
                {
                  id: 'storage_booker' as CustomerType,
                  label: 'Book Storage',
                  sub: 'Store luggage securely',
                  icon: Lock,
                  color: 'from-emerald-500 to-teal-600',
                },
              ]).map((ct) => (
                <button
                  key={`ct-${ct.id}`}
                  type="button"
                  onClick={() => setCustomerType(ct.id)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-150 ${
                    customerType === ct.id
                      ? ct.id === 'storage_booker' ?'border-emerald-500 bg-emerald-50' :'border-primary bg-secondary' :'border-border hover:border-muted-foreground'
                  }`}
                >
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${ct.color} text-white`}>
                    <ct.icon size={18} />
                  </div>
                  <div className="text-center">
                    <p className={`text-sm font-700 ${
                      customerType === ct.id
                        ? ct.id === 'storage_booker' ? 'text-emerald-700' : 'text-primary' :'text-foreground'
                    }`}>{ct.label}</p>
                    <p className="text-xs text-muted-foreground">{ct.sub}</p>
                  </div>
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">You can access both services after sign-up from your dashboard.</p>
          </div>
        )}

        {/* Host signup CTA — below role buttons */}
        {onHostSignup && (
          <div className="mt-3 p-4 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(5,150,105,0.08) 0%, rgba(16,185,129,0.05) 100%)', border: '1px solid rgba(5,150,105,0.2)' }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(5,150,105,0.15)' }}>
                <Warehouse size={17} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">Have a storage space?</p>
                <p className="text-xs text-muted-foreground">Earn by hosting travelers&apos; luggage</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onHostSignup}
              className="w-full py-2.5 text-white rounded-xl text-sm font-bold hover:opacity-90 active:scale-95 transition-all duration-150 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)', boxShadow: '0 4px 12px rgba(5,150,105,0.25)' }}
            >
              <Building2 size={15} />
              Apply to Become a Host
            </button>
          </div>
        )}
      </div>

      {/* Full name */}
      <div className="mb-4">
        <label htmlFor="signup-name" className="block text-sm font-600 text-foreground mb-1.5">Full Name</label>
        <input
          id="signup-name"
          type="text"
          placeholder="e.g. Wanjiku Kamau"
          className={`w-full px-3.5 py-2.5 rounded-xl border text-sm bg-background outline-none transition-colors placeholder:text-muted-foreground ${errors.fullName ? 'border-negative' : 'border-input focus:border-primary'}`}
          {...register('fullName', { required: 'Full name is required', minLength: { value: 3, message: 'Name must be at least 3 characters' } })}
        />
        {errors.fullName && <p className="mt-1 text-xs text-negative">{errors.fullName.message}</p>}
      </div>

      {/* Email */}
      <div className="mb-4">
        <label htmlFor="signup-email" className="block text-sm font-600 text-foreground mb-1.5">
          Email Address
          {(verificationMode === 'email' || verificationMode === 'both') && (
            <span className="ml-1 text-[10px] text-blue-600 font-600 bg-blue-50 px-1.5 py-0.5 rounded-full">Verified</span>
          )}
        </label>
        <input
          id="signup-email"
          type="email"
          placeholder="you@example.com"
          className={`w-full px-3.5 py-2.5 rounded-xl border text-sm bg-background outline-none transition-colors placeholder:text-muted-foreground ${errors.email ? 'border-negative' : 'border-input focus:border-primary'}`}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
          })}
        />
        {errors.email && <p className="mt-1 text-xs text-negative">{errors.email.message}</p>}
      </div>

      {/* Phone */}
      <div className="mb-4">
        <label htmlFor="signup-phone" className="block text-sm font-600 text-foreground mb-1.5">
          Phone Number
          {(verificationMode === 'phone' || verificationMode === 'both') && (
            <span className="ml-1 text-[10px] text-blue-600 font-600 bg-blue-50 px-1.5 py-0.5 rounded-full">Verified</span>
          )}
        </label>
        <p className="text-xs text-muted-foreground mb-1.5">Used for M-Pesa payments and delivery notifications</p>
        <div className="flex">
          <span className="px-3.5 py-2.5 bg-muted border border-r-0 border-input rounded-l-xl text-sm text-muted-foreground font-600">+254</span>
          <input
            id="signup-phone"
            type="tel"
            placeholder="7XX XXX XXX"
            className={`flex-1 px-3.5 py-2.5 rounded-r-xl border text-sm bg-background outline-none transition-colors placeholder:text-muted-foreground ${errors.phone ? 'border-negative' : 'border-input focus:border-primary'}`}
            {...register('phone', {
              required: 'Phone number is required',
              pattern: { value: /^[7][0-9]{8}$/, message: 'Enter a valid Kenyan number (7XXXXXXXX)' },
            })}
          />
        </div>
        {errors.phone && <p className="mt-1 text-xs text-negative">{errors.phone.message}</p>}
      </div>

      {/* Password */}
      <div className="mb-4">
        <label htmlFor="signup-password" className="block text-sm font-600 text-foreground mb-1.5">Password</label>
        <div className="relative">
          <input
            id="signup-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Min. 8 characters"
            className={`w-full px-3.5 py-2.5 pr-10 rounded-xl border text-sm bg-background outline-none transition-colors placeholder:text-muted-foreground ${errors.password ? 'border-negative' : 'border-input focus:border-primary'}`}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Password must be at least 8 characters' },
            })}
          />
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Toggle password">
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && <p className="mt-1 text-xs text-negative">{errors.password.message}</p>}
      </div>

      {/* Confirm password */}
      <div className="mb-4">
        <label htmlFor="signup-confirm" className="block text-sm font-600 text-foreground mb-1.5">Confirm Password</label>
        <div className="relative">
          <input
            id="signup-confirm"
            type={showConfirm ? 'text' : 'password'}
            placeholder="Repeat your password"
            className={`w-full px-3.5 py-2.5 pr-10 rounded-xl border text-sm bg-background outline-none transition-colors placeholder:text-muted-foreground ${errors.confirmPassword ? 'border-negative' : 'border-input focus:border-primary'}`}
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (v) => v === password || 'Passwords do not match',
            })}
          />
          <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Toggle confirm">
            {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.confirmPassword && <p className="mt-1 text-xs text-negative">{errors.confirmPassword.message}</p>}
      </div>

      {/* Traveler-only: ID verification */}
      {role === 'traveler' && (
        <div className="mb-4 p-4 bg-secondary rounded-xl border border-border">
          <div className="flex items-start gap-2 mb-3">
            <AlertTriangle size={15} className="text-warning shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-700 text-foreground">Identity Verification Required</p>
              <p className="text-xs text-muted-foreground mt-0.5">Travelers must verify their identity before carrying packages.</p>
            </div>
          </div>

          <div className="mb-3">
            <label className="block text-sm font-600 text-foreground mb-1.5">ID Document Type</label>
            <select
              className="w-full px-3.5 py-2.5 rounded-xl border border-input text-sm bg-background outline-none focus:border-primary"
              {...register('idType')}
            >
              <option value="national_id">National ID Card</option>
              <option value="passport">Passport</option>
              <option value="alien_id">Alien ID</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">Upload ID Document</label>
            <label className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary transition-colors">
              {idFileName ? (
                <>
                  <CheckCircle size={20} className="text-positive" />
                  <span className="text-sm text-positive font-600">{idFileName}</span>
                  <span className="text-xs text-muted-foreground">Click to replace</span>
                </>
              ) : (
                <>
                  <Upload size={20} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Click to upload front + back</span>
                  <span className="text-xs text-muted-foreground">JPG, PNG or PDF — max 5MB</span>
                </>
              )}
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                className="hidden"
                onChange={(e) => setIdFileName(e.target.files?.[0]?.name ?? null)}
              />
            </label>
          </div>
        </div>
      )}

      {/* Traveler terms */}
      {role === 'traveler' && (
        <div className="mb-4">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 w-4 h-4 rounded border-input accent-primary shrink-0"
              {...register('courierTerms', { required: 'You must accept the traveler terms' })}
            />
            <span className="text-xs text-muted-foreground leading-relaxed">
              I agree to only carry packages with declared legal contents, refuse suspicious packages, and comply with Kenya&apos;s regulations and MuyuLink&apos;s traveler code of conduct.
            </span>
          </label>
          {errors.courierTerms && <p className="mt-1 text-xs text-negative">{errors.courierTerms.message}</p>}
        </div>
      )}

      {/* Terms */}
      <div className="mb-5">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 w-4 h-4 rounded border-input accent-primary shrink-0"
            {...register('termsAccepted', { required: 'You must accept the terms to continue' })}
          />
          <span className="text-xs text-muted-foreground leading-relaxed">
            I agree to MuyuLink&apos;s{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary font-600 hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary font-600 hover:underline">Privacy Policy</a>
          </span>
        </label>
        {errors.termsAccepted && <p className="mt-1 text-xs text-negative">{errors.termsAccepted.message}</p>}
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-3 bg-primary text-primary-foreground rounded-xl text-sm font-700 hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Creating account...
          </>
        ) : role === 'traveler' ? (
          'Create Traveler Account'
        ) : customerType === 'storage_booker' ? (
          'Create Storage Account'
        ) : (
          'Create Sender Account'
        )}
      </button>

      <p className="mt-4 text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button type="button" onClick={onSwitchToLogin} className="text-primary font-600 hover:underline">
          Sign in
        </button>
      </p>
    </form>
  );
}